// @flow
import {
    FETCH_USERTOURS_REQUEST,
    FETCH_USERTOURS_SUCCESS,
    UPDATE_USERTOUR_CHECKLIST_SUCCESS,
    UPDATE_USERTOUR_CHECKLIST_REQUEST,
    UPDATE_USERTOUR_CHECKLIST_ERROR
} from 'common/constants/actionTypes';
import * as Immutable from 'immutable';
import VisitTableUtils from 'common/utils/VisitTableUtils';
import { VisitTypeEnum } from 'common/enums/VisitEnums';
import { RouteRestriction } from 'common/models/internal/RouteRestriction';

export const userTour = (state = Immutable.fromJS({ records: {} }), action) => {
    let clientId;
    let userId;
    let date;
    let shift;
    if (action.params) {
        ({ clientId, userId, date, shift } = action.params);
    }
    const path = ['records', clientId, userId, date, shift];
    switch (action.type) {
        case FETCH_USERTOURS_REQUEST: {
            return state.setIn([...path, 'status', 'isLoadingUserTours'], true);
        }

        case FETCH_USERTOURS_SUCCESS: {
            var userTours = action.response;
            var visitIdToVisitMap;
            var visitIdToReducedVisitMap;
            (userTours.tours || []).forEach(tour => {
                tour.reducedUserTourVisits = reduceUserTourVisits(tour.visits);
                visitIdToVisitMap = getVisitIdToVisitMap(tour.visits);
                (tour.sequenceEdits || []).forEach(edit => {
                    var key = [edit.clientId, edit.taskId, edit.visitId];
                    var visit = visitIdToVisitMap.get(key.join('/'));
                    if (visit) {
                        visit.isSequenceEdited = true;
                    }
                });
                visitIdToReducedVisitMap = getVisitIdToVisitMap(
                    tour.reducedUserTourVisits
                );
                tour.plannedTour.visits = joinTourVisitIdsWithFullVisits(
                    tour.plannedTour.visits,
                    visitIdToVisitMap,
                    visitIdToReducedVisitMap
                );
                tour.currentTour.visits = joinTourVisitIdsWithFullVisits(
                    tour.currentTour.visits,
                    visitIdToVisitMap,
                    visitIdToReducedVisitMap
                );
                tour.routeRestrictions = tour.routeRestrictions.map(
                    routeRestriction => {
                        return RouteRestriction.fromJS(routeRestriction);
                    }
                );
            });

            userTours = Immutable.fromJS(userTours);
            userTours = userTours.get('tours').map(userTour => {
                const newUserTour = userTour.set(
                    'visits',
                    VisitTableUtils.getVisitTransactionStatus(
                        userTour.get('visits')
                    )
                );
                return newUserTour;
            });
            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'entity'], userTours);
                mutableState.setIn(
                    [...path, 'status', 'isLoadingUserTours'],
                    false
                );
            });
        }
        case UPDATE_USERTOUR_CHECKLIST_REQUEST: {
            return state.setIn(
                [...path, 'status', 'isTourChecklistLoading'],
                true
            );
        }
        case UPDATE_USERTOUR_CHECKLIST_ERROR: {
            var errors = action.response;
            return state.withMutations(mutableState => {
                mutableState.setIn(
                    [...path, 'status', 'isTourChecklistLoading'],
                    false
                );
                mutableState.setIn(
                    [...path, 'status', 'checklistErrors'],
                    errors
                );
            });
        }
        case UPDATE_USERTOUR_CHECKLIST_SUCCESS: {
            var checklistItems = action.response;
            var { tourId } = action.params;
            const oldUserTours = state.getIn([...path, 'entity']);
            const newUserTours = oldUserTours.map(userTour => {
                if (userTour.getIn(['tourId', 'tourId']) === tourId) {
                    return userTour.set(
                        'checklistValues',
                        Immutable.fromJS(checklistItems.checklistValues)
                    );
                }
                return userTour;
            });
            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'entity'], newUserTours);
                mutableState.setIn(
                    [...path, 'status', 'isTourChecklistLoading'],
                    false
                );
                mutableState.deleteIn([...path, 'status', 'checklistErrors']);
            });
        }
        default:
            return state;
    }
};

const getVisitIdToVisitMap = visits => {
    var visitIdToVisitMap = new Map();
    var keys;
    visits.forEach(visit => {
        keys = [visit.clientId, visit.sourceId, visit.id];
        visitIdToVisitMap.set(keys.join('/'), visit);
    });
    return visitIdToVisitMap;
};

const joinTourVisitIdsWithFullVisits = (
    visits,
    visitIdToVisitMap,
    visitIdToReducedVisitMap
) => {
    var key;
    var visitObject;
    var reducedVisitObject;
    return visits.map(visit => {
        key = [visit.id.clientId, visit.id.taskId, visit.id.visitId].join('/');
        visitObject = visitIdToVisitMap.get(key);
        reducedVisitObject = visitIdToReducedVisitMap.get(key);
        if (!visitObject) {
            throw new Error(`Key Not Found: ${key}`);
        }
        visit.visitObject = visitObject;
        visit.reducedVisitObject = reducedVisitObject;
        return visit;
    });
};

const reduceUserTourVisits = userTourVisits => {
    var reducedUserTourVisits = [];
    var pickupHomebaseVisit = null;
    var dropHomebaseVisit = null;

    for (var i = 0; i < userTourVisits.length; i++) {
        var visit = userTourVisits[i];
        const lastHomebaseVisit = pickupHomebaseVisit || dropHomebaseVisit;
        const isDifferentAddress =
            lastHomebaseVisit &&
            lastHomebaseVisit.chosenLocation.address.id !==
                visit.chosenLocation.address.id;
        if (visit.visitType !== VisitTypeEnum.Homebase || isDifferentAddress) {
            // If the visit is not a homebase, add the merged ones into the reduced list.
            // If the visit is a homebase, but different from the homebases which we have
            // merged it now, add the merged ones into the reduced list.
            if (pickupHomebaseVisit) {
                reducedUserTourVisits.push(pickupHomebaseVisit);
            }
            if (dropHomebaseVisit) {
                reducedUserTourVisits.push(dropHomebaseVisit);
            }
            pickupHomebaseVisit = null;
            dropHomebaseVisit = null;
        }

        if (visit.visitType !== VisitTypeEnum.Homebase) {
            //If non homebase, nothing need to be reduces, just push to the list
            reducedUserTourVisits.push(visit);
            continue;
        }

        var { transactionType } = visit;

        if (transactionType === 'PICKUP') {
            if (!pickupHomebaseVisit) {
                visit.mergedVisits = [];
                pickupHomebaseVisit = visit;
                continue;
            }
            pickupHomebaseVisit.mergedVisits.push(visit);
            pickupHomebaseVisit.currentEta = visit.currentEta;
            const { chosenLocation } = pickupHomebaseVisit;
            const { slot } = chosenLocation;
            const { transactionDuration } = slot;
            pickupHomebaseVisit.chosenLocation.slot.transactionDuration =
                transactionDuration +
                visit.chosenLocation.slot.transactionDuration;
            continue;
        } else if (transactionType === 'DROP') {
            if (!dropHomebaseVisit) {
                visit.mergedVisits = [];
                dropHomebaseVisit = visit;
                continue;
            }
            dropHomebaseVisit.mergedVisits.push(visit);
            dropHomebaseVisit.currentEta = visit.currentEta;
            const { chosenLocation } = dropHomebaseVisit;
            const { slot } = chosenLocation;
            const { transactionDuration } = slot;
            dropHomebaseVisit.chosenLocation.slot.transactionDuration =
                transactionDuration +
                visit.chosenLocation.slot.transactionDuration;
            continue;
        }
        throw new Error(`Unknown transactionType: ${transactionType}`);
    }

    // For last homebase visits to get added as well as for only homebase visits tour (rare)
    if (pickupHomebaseVisit) {
        reducedUserTourVisits.push(pickupHomebaseVisit);
    }
    if (dropHomebaseVisit) {
        reducedUserTourVisits.push(dropHomebaseVisit);
    }

    var index = 1;
    var labelIndex = 1;
    var homebaseLabelIndex = 1;
    var userVisitLabelIndex = 1;
    reducedUserTourVisits.forEach(visit => {
        visit.index = index++;
        if (
            visit.visitType !== VisitTypeEnum.Homebase &&
            visit.visitType !== VisitTypeEnum.User
        ) {
            visit.labelIndex = labelIndex;
            labelIndex++;
        } else if (visit.visitType === VisitTypeEnum.Homebase) {
            visit.labelIndex = `Homebase/${homebaseLabelIndex}`;
            homebaseLabelIndex += homebaseLabelIndex;
        } else if (visit.visitType === VisitTypeEnum.User) {
            visit.labelIndex = `User/${userVisitLabelIndex}`;
            userVisitLabelIndex += userVisitLabelIndex;
        }
    });

    return reducedUserTourVisits;
};
